import React, { useEffect, useRef } from 'react';

function HandpayBarChart({ d3, data }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!d3 || !containerRef.current || !data?.length) return;

    const container = containerRef.current;
    container.innerHTML = '';

    const margin = { top: 10, right: 40, bottom: 50, left: 40 };
    const parent = container.parentElement;
    const cw =
      container.clientWidth ||
      (parent && parent.clientWidth) ||
      container.getBoundingClientRect().width ||
      600;
    const width = cw - margin.left - margin.right;
    const height = Math.round(width * 0.6);

    const x = d3
      .scaleTime()
      .domain(d3.extent(data, (d) => d.date))
      .range([0, width]);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.handpays)])
      .nice()
      .range([height, 0]);

    const computeBarWidth = (scale, scaleDates, index) => {
      const x0 = scale(scaleDates[index]);
      const prev = index > 0 ? scale(scaleDates[index - 1]) : null;
      const next = index < scaleDates.length - 1 ? scale(scaleDates[index + 1]) : null;
      let gap;
      if (prev != null && next != null) gap = Math.min(x0 - prev, next - x0);
      else if (prev != null) gap = x0 - prev;
      else if (next != null) gap = next - x0;
      else gap = 10;
      const raw = Math.abs(gap) * 1.6;
      return Math.max(3, Math.min(24, raw));
    };

    const dates = data.map((d) => d.date);

    // Pad by half the actual bar width at each end so first/last bars are fully visible
    const halfFirst = dates.length ? computeBarWidth(x, dates, 0) / 2 : 6;
    const halfLast = dates.length ? computeBarWidth(x, dates, dates.length - 1) / 2 : 6;
    x.range([halfFirst, width - halfLast]);

    const svg = d3
      .select(container)
      .append('svg')
      .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .style('width', '100%')
      .style('height', '100%');

    const chartGroup = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const clipId = 'handpay-clip';
    svg.append('defs')
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      // clip coordinates are relative to the chartGroup (which is already translated by margins)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', height);

    const plotGroup = chartGroup.append('g').attr('clip-path', `url(#${clipId})`);

    const xAxis = d3.axisBottom(x).tickFormat(d3.timeFormat('%b %d')).ticks(3);
    const yAxis = d3.axisLeft(y);

    chartGroup
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .attr('class', 'x-axis')
      .call(xAxis)
      .selectAll('text')
      .style('font-size', '16px');

    chartGroup
      .append('g')
      .attr('class', 'y-axis')
      .call(yAxis)
      .selectAll('text')
      .style('font-size', '16px');

    const bars = plotGroup
      .selectAll('rect')
      .data(data)
      .enter()
      .append('rect')
      .attr('y', (d) => y(d.handpays))
      .attr('height', (d) => height - y(d.handpays))
      .attr('fill', '#76ace0')
      .attr('x', (d, i) => x(dates[i]) - computeBarWidth(x, dates, i) / 2)
      .attr('width', (d, i) => computeBarWidth(x, dates, i));

    const dataMin = d3.min(dates);
    const dataMax = d3.max(dates);

    const zoom = d3
      .zoom()
      .scaleExtent([1, 10])
      .extent([
        [margin.left, margin.top],
        [margin.left + width, margin.top + height],
      ])
      .translateExtent([
        [margin.left + x(dataMin) - halfFirst, margin.top],
        [margin.left + x(dataMax) + halfLast, margin.top + height],
      ])
      .on('zoom', (event) => {
        let t = event.transform;
        // lock vertical panning
        if (t.y !== 0) {
          t = d3.zoomIdentity.translate(t.x, 0).scale(t.k);
          zoomPane.call(zoom.transform, t);
          return;
        }
        const newX = t.rescaleX(x);
        const newY = y; // keep Y fixed to avoid jumps

        chartGroup
          .select('.x-axis')
          .call(d3.axisBottom(newX).tickFormat(d3.timeFormat('%b %d')).ticks(3))
          .selectAll('text')
          .style('font-size', '16px');
        // y-axis remains unchanged

        bars
          .attr('y', (d) => newY(d.handpays))
          .attr('height', (d) => height - newY(d.handpays))
          .attr('x', (d, i) => newX(dates[i]) - computeBarWidth(newX, dates, i) / 2)
          .attr('width', (d, i) => computeBarWidth(newX, dates, i));
      });

    const zoomPane = svg
      .append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('x', margin.left)
      .attr('y', margin.top)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .call(zoom);
  }, [d3, data]);

  return (
    <div className="chart-card">
      <h2>NUMBER OF HANDPAYS PER DAY</h2>
      <div ref={containerRef} />
    </div>
  );
}

export default HandpayBarChart;
